import { HStack, PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { Filter } from '@/components/DataTable/FilterControls'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import { Note } from '@/components/Note'
import useStatementLegalNameFilter from '@/hooks/filters/credit/useStatementLegalNameFilter'
import {
  googleFinancialStatementsExportQueryOptions,
  googleFinancialStatementsSearchSchema,
  useGoogleFinancialStatements,
} from '@/hooks/queries/credit/useGoogleFinancialStatements'
import { useTablePagination } from '@/hooks/useTablePagination'
import { getClearedFilters } from '@/lib/utils/filters'
import { queryClient } from '@/main'
import { GOOGLE_FINANCIAL_STATEMENT_TYPES, GoogleFinancialStatementType } from '@repo/constants'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/components'
import { createFileRoute } from '@tanstack/react-router'
import { InfoIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GfsImportHistoryPanel } from './-components/GfsImportHistoryPanel'
import { GoogleFinancialStatementsDataTable } from './-components/GoogleFinancialStatementsDataTable'
import { ImportGoogleFinancialStatementsDialog } from './-components/ImportGoogleFinancialStatementsDialog'

export const Route = createFileRoute('/_auth/_modules/credit/google-financial-statements/')({
  component: RouteComponent,
  validateSearch: (search) => googleFinancialStatementsSearchSchema.parse(search),
})

const DEFAULT_TYPE = GOOGLE_FINANCIAL_STATEMENT_TYPES[0]

function RouteComponent() {
  const { t } = useTranslation(['credit', 'common'])
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const selectedType: GoogleFinancialStatementType = search.type ?? DEFAULT_TYPE

  const { legalNameFilter, legalNames, setLegalNames } = useStatementLegalNameFilter(
    search.legalName,
  )
  const [keyword, setKeyword] = useState<string>(search.keyword ?? '')
  // Imports are processed asynchronously by the worker; on success we show an info banner
  // telling the user to refresh their browser once processing has had a moment.
  const [importSucceeded, setImportSucceeded] = useState(false)

  const selectedFilters = {
    legalName: legalNames?.length ? legalNames : undefined,
  }

  const filters = [legalNameFilter]

  const googleFinancialStatementsQuery = useGoogleFinancialStatements({
    type: selectedType,
    keyword: search.keyword,
    legalName: search.legalName,
    page: search.page ?? 1,
    pageSize,
    sort: search.sort,
  })

  const handleTypeChange = (value: string) => {
    // Keep the page and filters; only the metric (and thus the trend/value) changes.
    navigate({ search: { ...search, type: value as GoogleFinancialStatementType } })
  }

  const showResults = () => {
    navigate({
      search: {
        ...search,
        ...selectedFilters,
        keyword: keyword || undefined,
        page: 1,
      },
    })
  }

  const resetAllFilters = () => {
    setKeyword('')
    setLegalNames([])

    const clearedFilters = getClearedFilters(selectedFilters)

    navigate({
      search: {
        ...search,
        ...clearedFilters,
        keyword: undefined,
        page: 1,
      },
    })
  }

  const getCsvExport = () =>
    queryClient.ensureQueryData(
      googleFinancialStatementsExportQueryOptions({
        type: selectedType,
        keyword: search.keyword,
        legalName: search.legalName,
      }),
    )

  return (
    <PageContainer maxWidth="wide">
      <Spread>
        <PageHeader>{t('googleFinancialStatements.pageHeader')}</PageHeader>
        <HStack gap="sm">
          <GfsImportHistoryPanel />

          <ImportGoogleFinancialStatementsDialog onUploadSuccess={() => setImportSucceeded(true)} />

          <ExportCsvButton
            getCsvExport={getCsvExport}
            rowCount={googleFinancialStatementsQuery.data?.rowCount ?? 0}
            label={t('export.viewData', { ns: 'common' })}
          />
        </HStack>
      </Spread>

      {importSucceeded && (
        <Note
          tone="info"
          icon={InfoIcon}
          note={t('googleFinancialStatements.import.successBanner')}
        />
      )}

      <Card>
        <CardContent className="pt-6">
          <GoogleFinancialStatementsDataTable
            type={selectedType}
            filters={filters as Filter<unknown>[]}
            search={{
              value: keyword,
              setValue: setKeyword,
              placeholder: t('googleFinancialStatements.searchPlaceholder'),
            }}
            actions={
              <Select value={selectedType} onValueChange={handleTypeChange}>
                <SelectTrigger className="bg-background w-[220px]">
                  <SelectValue placeholder={t('googleFinancialStatements.selectType')} />
                </SelectTrigger>
                <SelectContent>
                  {GOOGLE_FINANCIAL_STATEMENT_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`googleFinancialStatements.type.${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            googleFinancialStatementsQuery={googleFinancialStatementsQuery}
            onShowResults={showResults}
            onResetAllFilters={resetAllFilters}
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
